export type DiscountType = 'Discount Percent' | 'Discount Amount';

/**
 * Formats a currency value using Intl.NumberFormat.
 * Includes error handling with a fallback format.
 */
export function formatCurrency(amount: number, currencyCode: string, locale: string): string {
  if (isNaN(amount)) return String(amount);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol'
    }).format(amount);
  } catch (error) {
    // Fallback if Intl.NumberFormat fails
    console.error('Currency formatting error:', error);
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

/**
 * Formats a discount value for display.
 * - For percentages: removes trailing .00 (e.g., "30.00" -> "30", but "30.5" stays "30.5")
 * - For amounts: returns as-is (currency formatting done by formatDiscountDisplay)
 */
export function formatDiscountValue(value: string | number, discountType: DiscountType): string {
  const strValue = String(value);

  if (discountType === 'Discount Percent') {
    const numValue = parseFloat(strValue);
    // Remove trailing .00 for whole numbers
    return numValue % 1 === 0 ? numValue.toString() : strValue;
  }

  return strValue;
}

/**
 * Formats a complete discount string for display.
 * - Percent: "30%" or "30.5%"
 * - Amount: "$5.00" (uses Intl.NumberFormat for currency formatting)
 */
export function formatDiscountDisplay(
  value: string | number,
  discountType: DiscountType,
  currencyCode: string = 'USD',
  locale: string = 'en-US'
): string {
  const formattedValue = formatDiscountValue(value, discountType);

  if (discountType === 'Discount Percent') {
    return `${formattedValue}%`;
  }

  // For amounts, use formatCurrency helper
  const amount = parseFloat(formattedValue);
  return formatCurrency(amount, currencyCode, locale);
}
